import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TenantUsageDay, TenantUsageResponse } from "@rua-watch/shared";
import { useActiveTenant, useTenantUsage } from "../api/hooks";
import { useTheme } from "../components/theme";

// Chart palette — single data series in the repo's emerald primary
// (matches the QuotaCard bars); the monthly cap is a dashed, labelled
// rose reference line (secondary encoding, never colour-alone).
const DATA_COLOR = "#059669"; // emerald-600 — legible on white and slate-900
const CAP_COLOR = "#e11d48"; // rose-600
const GRID = "#e2e8f0";

// Compact y-axis ticks: 12.3k / 1.2M so the axis never wraps.
function fmtAxis(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

// Plan & usage — the active tenant's consumption view, moved out of
// Settings into its own Admin tab. Three control metrics (messages,
// domains, seats): current month day-by-day + prior months aggregated.
// The message cap is soft — over-limit is surfaced, never enforced.

const UNLIMITED = 999_999_999;
const fmt = (n: number) => n.toLocaleString();
const isUnlimited = (limit: number) => limit >= UNLIMITED;

export function AdminUsageRoute() {
  const tenant = useActiveTenant();
  const usage = useTenantUsage();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">Plan &amp; usage</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Consumption of the three control metrics for this workspace.
          </p>
        </div>
        {tenant.data ? (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {tenant.data.plan} · {tenant.data.retentionDays}d retention
          </span>
        ) : null}
      </div>

      {usage.isPending ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading usage…</p>
      ) : usage.error || !usage.data ? (
        <p className="text-sm text-rose-600 dark:text-rose-400">Failed to load usage.</p>
      ) : (
        <UsageBody data={usage.data} />
      )}

      <p className="text-xs text-slate-500 dark:text-slate-400">
        The monthly message cap is a soft limit — reports are always ingested; going over only
        flags this workspace for review. Plan changes are managed by Etus support.
      </p>
    </div>
  );
}

function UsageBody({ data }: { data: TenantUsageResponse }) {
  const { limits, current, daily, monthly } = data;
  // Recent day first — surfaces today (the live row) at the top.
  const dailyDesc = [...daily].reverse();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <QuotaCard
          label="Messages"
          note="this month"
          icon={<IconMail />}
          current={current.messagesThisMonth}
          limit={limits.maxMessagesMonth}
        />
        <QuotaCard
          label="Domains"
          note="in use"
          icon={<IconGlobe />}
          current={current.domains}
          limit={limits.maxDomains}
        />
        <QuotaCard
          label="Seats"
          note="in use"
          icon={<IconUsers />}
          current={current.seats}
          limit={limits.maxSeats}
        />
      </div>

      <DailyMessageCharts daily={daily} cap={limits.maxMessagesMonth} />

      <div>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          This month · daily breakdown
        </h3>
        {dailyDesc.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No activity yet this month.</p>
        ) : (
          <UsageTable
            head={["Day", "Messages", "Domains", "Seats"]}
            rows={dailyDesc.map((d) => ({
              key: d.day,
              live: d.live,
              cells: [d.day, fmt(d.messages), fmt(d.domains), fmt(d.seats)],
            }))}
          />
        )}
      </div>

      <div>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Previous months
        </h3>
        {monthly.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No history yet.</p>
        ) : (
          <UsageTable
            head={["Month", "Messages", "Domains (peak)", "Seats (peak)"]}
            rows={monthly.map((m) => ({
              key: m.month,
              cells: [m.month, fmt(m.messages), fmt(m.domainsPeak), fmt(m.seatsPeak)],
            }))}
          />
        )}
      </div>
    </div>
  );
}

// Two single-series charts for the daily message flow (the metric with a
// cap). Domains/seats are near-constant state and stay tabular. Bars read
// as discrete daily volume; the cumulative area climbs toward the dashed
// monthly-cap reference so the soft limit is legible at a glance.
function DailyMessageCharts({ daily, cap }: { daily: TenantUsageDay[]; cap: number }) {
  const { resolved } = useTheme();
  const dark = resolved === "dark";
  if (daily.length === 0) return null;

  const perDay = daily.map((d) => ({ day: d.day, dd: d.day.slice(8), messages: d.messages }));
  let running = 0;
  const cumulative = daily.map((d) => {
    running += d.messages;
    return { day: d.day, dd: d.day.slice(8), cumulative: running };
  });
  const finiteCap = cap < UNLIMITED;
  const capHeadroom = finiteCap ? Math.max(cap, running) : undefined;

  const tooltip = {
    contentStyle: {
      background: dark ? "#0f172a" : "#fff",
      borderColor: dark ? "#334155" : GRID,
      borderRadius: 8,
      fontSize: 12,
    },
    labelStyle: { color: dark ? "#e2e8f0" : "#0f172a" },
    labelFormatter: (dd: string, payload: readonly { payload?: { day?: string } }[]) =>
      payload?.[0]?.payload?.day ?? dd,
  } as const;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
        This month · messages
      </h3>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ChartCard title="Per day">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={perDay} barCategoryGap={2} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3" stroke={GRID} className="dark:stroke-slate-700" />
              <XAxis dataKey="dd" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} tickFormatter={fmtAxis} />
              <Tooltip
                {...tooltip}
                cursor={{ fill: "currentColor", opacity: 0.06 }}
                formatter={(v: number) => [fmt(v), "Messages"]}
              />
              <Bar dataKey="messages" fill={DATA_COLOR} radius={[4, 4, 0, 0]} name="Messages" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={finiteCap ? "Cumulative vs. monthly cap" : "Cumulative"}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={cumulative} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="usageCumFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={DATA_COLOR} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={DATA_COLOR} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3" stroke={GRID} className="dark:stroke-slate-700" />
              <XAxis dataKey="dd" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={40}
                tickFormatter={fmtAxis}
                domain={[0, capHeadroom ?? "auto"]}
              />
              <Tooltip {...tooltip} formatter={(v: number) => [fmt(v), "Cumulative"]} />
              {finiteCap ? (
                <ReferenceLine
                  y={cap}
                  stroke={CAP_COLOR}
                  strokeDasharray="4 4"
                  label={{ value: `cap ${fmtAxis(cap)}`, fontSize: 11, fill: CAP_COLOR, position: "insideTopRight" }}
                />
              ) : null}
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke={DATA_COLOR}
                strokeWidth={2}
                fill="url(#usageCumFill)"
                name="Cumulative"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
      <div className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-300">{title}</div>
      <div className="h-56">{children}</div>
    </div>
  );
}

function QuotaCard({
  label,
  note,
  icon,
  current,
  limit,
}: {
  label: string;
  note: string;
  icon: React.ReactNode;
  current: number;
  limit: number;
}) {
  const unlimited = isUnlimited(limit);
  const pct = unlimited || limit <= 0 ? 0 : Math.min(100, Math.round((current / limit) * 100));
  const over = !unlimited && current > limit;
  const near = !over && pct >= 80;
  const barTone = over ? "bg-rose-500" : near ? "bg-amber-500" : "bg-emerald-600";

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 p-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
          <span className="text-slate-400 dark:text-slate-500">{icon}</span>
          {label}
        </span>
        {over ? <Badge tone="bad">over</Badge> : near ? <Badge tone="warn">{pct}%</Badge> : null}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-xl font-semibold text-slate-900 dark:text-slate-100">{fmt(current)}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          / {unlimited ? "∞" : fmt(limit)} {note}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div className={`h-full rounded-full ${barTone}`} style={{ width: `${unlimited ? 0 : pct}%` }} />
      </div>
    </div>
  );
}

function UsageTable({
  head,
  rows,
}: {
  head: string[];
  rows: { key: string; live?: boolean; cells: string[] }[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {head.map((h, i) => (
              <th key={h} className={`px-3 py-2 font-medium ${i === 0 ? "" : "text-right"}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-slate-100 last:border-0 dark:border-slate-800/60">
              {r.cells.map((cell, i) => (
                <td
                  key={i}
                  className={`px-3 py-1.5 ${
                    i === 0
                      ? "text-slate-700 dark:text-slate-300"
                      : "text-right tabular-nums text-slate-800 dark:text-slate-200"
                  }`}
                >
                  {i === 0 ? (
                    <span className="inline-flex items-center gap-2">
                      {cell}
                      {r.live ? <Badge tone="ok">today</Badge> : null}
                    </span>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: "ok" | "warn" | "bad" }) {
  const cls =
    tone === "ok"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
      : tone === "warn"
      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
      : "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200";
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-[11px] uppercase tracking-wider ${cls}`}>
      {children}
    </span>
  );
}

// Inline lucide-style icons — repo inlines SVGs rather than pulling in
// lucide-react (see Sidebar). Decorative, so aria-hidden.
function svgProps(size = 14) {
  return {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: "inline",
    width: size,
    height: size,
    "aria-hidden": true,
  };
}
function IconMail() {
  return (
    <svg {...svgProps()}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  );
}
function IconGlobe() {
  return (
    <svg {...svgProps()}>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 0 20 15.3 15.3 0 0 1 0-20Z" />
    </svg>
  );
}
function IconUsers() {
  return (
    <svg {...svgProps()}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
